import React, { useState } from 'react';
import { ClockIcon, MapPinIcon, TruckIcon, InfoIcon, AlertCircleIcon, CheckCircleIcon, ClipboardListIcon, UserIcon, BuildingIcon, XIcon, NavigationIcon, MessageSquareIcon, ChevronDownIcon, ChevronUpIcon, CalendarIcon, PencilIcon, FileTextIcon, HistoryIcon } from 'lucide-react';
import { SafetyAlert } from './SafetyAlert';
import { JobCard } from './JobCard';
import { TruckSelector } from './TruckSelector';
import { GeoClockInScreen } from './GeoClockInScreen';
import { JobTrackingScreen } from './JobTrackingScreen';
import { CompletedJobs } from './CompletedJobs';
import { MessageNotifications } from '../../messaging/MessageNotifications';
import { useMessages } from '../../messaging/MessageContext';
import { JobMessagingNotification } from '../../messaging/JobMessagingNotification';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface Job {
  id: string;
  date: string;
  customer: string;
  jobName: string;
  jobNumber: string;
  startTime: string;
  travelTime: string;
  loadLocation: {
    name: string;
    address: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  dumpLocation: {
    name: string;
    address: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  material: string;
  truckType: string;
  goals: {
    loads: number;
    tonnage?: number;
  };
  foreman: {
    name: string;
    phone: string;
  };
  notes: {
    contractor?: string;
    dispatch?: string;
  };
  status: 'pending' | 'in-progress' | 'completed';
  ticketNumber?: string;
}
export const DriverDashboard = () => {
  const [showSafetyAlert, setShowSafetyAlert] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [truckNumber, setTruckNumber] = useState('T-007');
  const [showTruckSelector, setShowTruckSelector] = useState(false);
  const [showGeoClockIn, setShowGeoClockIn] = useState(false);
  const [showJobTracking, setShowJobTracking] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [clockedIn, setClockedIn] = useState(false);
  const [showMessageCenter, setShowMessageCenter] = useState(false);
  const [initialContactId, setInitialContactId] = useState<string | undefined>();
  // Sample completed jobs data - this would come from your API in a real application
  const completedJobs = [{
    id: 'completed-1',
    date: '2024-01-10',
    completedDate: '2024-01-10',
    customer: 'ABC Construction',
    jobName: 'Downtown Commercial Complex Foundation',
    jobNumber: 'DCC-2023-095',
    material: 'Concrete',
    truckType: 'Dump Truck',
    loadLocation: {
      name: 'West Valley Materials Yard'
    },
    dumpLocation: {
      name: 'Downtown SLC Project'
    },
    foreman: {
      name: 'John Smith',
      signature: true
    },
    stats: {
      loads: 12,
      tonnage: 360,
      hours: 8.5,
      startTime: '07:00',
      endTime: '15:30'
    },
    status: 'completed',
    ticketNumber: '500001',
    paymentStatus: 'paid'
  }, {
    id: 'completed-2',
    date: '2024-01-05',
    completedDate: '2024-01-05',
    customer: 'Mountain Development LLC',
    jobName: 'Residential Development Phase 1',
    jobNumber: 'RES-2023-087',
    material: 'Road Base',
    truckType: 'Side Dump',
    loadLocation: {
      name: 'Gravel Yard East'
    },
    dumpLocation: {
      name: 'Sugar House Development'
    },
    foreman: {
      name: 'Sarah Williams',
      signature: true
    },
    stats: {
      loads: 8,
      tonnage: 240,
      hours: 7.25,
      startTime: '07:30',
      endTime: '14:45'
    },
    status: 'completed',
    ticketNumber: '500002',
    paymentStatus: 'paid'
  }, {
    id: 'completed-3',
    date: '2023-12-28',
    completedDate: '2023-12-28',
    customer: 'City Public Works',
    jobName: 'Main Street Repaving Project',
    jobNumber: 'CITY-2023-042',
    material: 'Asphalt',
    truckType: 'Dump Truck',
    loadLocation: {
      name: 'Asphalt Plant North'
    },
    dumpLocation: {
      name: 'Main Street Construction Zone'
    },
    foreman: {
      name: 'Robert Johnson',
      signature: false
    },
    stats: {
      loads: 6,
      tonnage: 180,
      hours: 6.5,
      startTime: '08:00',
      endTime: '14:30'
    },
    status: 'completed',
    ticketNumber: '500003',
    paymentStatus: 'pending'
  }, {
    id: 'completed-4',
    date: '2023-12-20',
    completedDate: '2023-12-20',
    customer: 'ABC Construction',
    jobName: 'Highway 89 Expansion',
    jobNumber: 'HWY-2023-076',
    material: 'Gravel',
    truckType: 'Super Dump',
    loadLocation: {
      name: 'North Quarry'
    },
    dumpLocation: {
      name: 'Highway 89 Mile Marker 12'
    },
    foreman: {
      name: 'Michael Davis',
      signature: true
    },
    stats: {
      loads: 14,
      tonnage: 420,
      hours: 9.75,
      startTime: '06:30',
      endTime: '16:15'
    },
    status: 'completed',
    ticketNumber: '500004',
    paymentStatus: 'paid'
  }, {
    id: 'completed-5',
    date: '2023-12-15',
    completedDate: '2023-12-15',
    customer: 'Mountain Development LLC',
    jobName: 'Commercial Plaza Foundation',
    jobNumber: 'COM-2023-064',
    material: 'Sand',
    truckType: 'Belly Dump',
    loadLocation: {
      name: 'South Sand Pit'
    },
    dumpLocation: {
      name: 'Commercial Plaza Site'
    },
    foreman: {
      name: 'David Wilson',
      signature: true
    },
    stats: {
      loads: 10,
      tonnage: 300,
      hours: 8,
      startTime: '07:00',
      endTime: '15:00'
    },
    status: 'completed',
    ticketNumber: '500005',
    paymentStatus: 'paid'
  }, {
    id: 'completed-6',
    date: '2023-12-08',
    completedDate: '2023-12-08',
    customer: 'City Public Works',
    jobName: 'Downtown Water Main Replacement',
    jobNumber: 'CITY-2023-038',
    material: 'Backfill',
    truckType: 'Dump Truck',
    loadLocation: {
      name: 'City Materials Yard'
    },
    dumpLocation: {
      name: 'Downtown Water Main Project'
    },
    foreman: {
      name: 'Thomas Brown',
      signature: false
    },
    stats: {
      loads: 7,
      tonnage: 210,
      hours: 7.5,
      startTime: '07:30',
      endTime: '15:00'
    },
    status: 'completed',
    ticketNumber: '500006',
    paymentStatus: 'pending'
  }, {
    id: 'completed-7',
    date: '2023-12-01',
    completedDate: '2023-12-01',
    customer: 'ABC Construction',
    jobName: 'School Expansion Project',
    jobNumber: 'EDU-2023-052',
    material: 'Road Base',
    truckType: 'Dump Truck',
    loadLocation: {
      name: 'West Valley Materials Yard'
    },
    dumpLocation: {
      name: 'Lincoln Elementary School'
    },
    foreman: {
      name: 'Jennifer Adams',
      signature: true
    },
    stats: {
      loads: 9,
      tonnage: 270,
      hours: 8.25,
      startTime: '07:00',
      endTime: '15:15'
    },
    status: 'completed',
    ticketNumber: '500007',
    paymentStatus: 'paid'
  }, {
    id: 'completed-8',
    date: '2023-11-22',
    completedDate: '2023-11-22',
    customer: 'Mountain Development LLC',
    jobName: 'Mountain View Condos Site Prep',
    jobNumber: 'RES-2023-078',
    material: 'Topsoil',
    truckType: 'Side Dump',
    loadLocation: {
      name: 'Topsoil Depot'
    },
    dumpLocation: {
      name: 'Mountain View Condos Site'
    },
    foreman: {
      name: 'Richard Taylor',
      signature: true
    },
    stats: {
      loads: 5,
      tonnage: 150,
      hours: 6,
      startTime: '08:00',
      endTime: '14:00'
    },
    status: 'completed',
    ticketNumber: '500008',
    paymentStatus: 'paid'
  }];
  const {
    getJobContacts
  } = useMessages();
  // Get today's date in YYYY-MM-DD format for comparison
  const today = new Date().toISOString().split('T')[0];
  // Tomorrow's date for upcoming jobs
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowString = tomorrow.toISOString().split('T')[0];
  // Sample job data - this would come from your API in a real application
  const jobs: Job[] = [
  // Today's Job - Turned off geo punch requirement
  {
    id: 'job-1',
    date: today,
    customer: 'ABC Construction',
    jobName: 'Downtown Commercial Complex Phase 1',
    jobNumber: 'DCC-2024-001',
    startTime: '07:00',
    travelTime: '25',
    loadLocation: {
      name: 'West Valley Materials Yard',
      address: '3500 W 4500 S, West Valley City, UT 84119',
      coordinates: {
        lat: 40.6789,
        lng: -112.0123
      }
    },
    dumpLocation: {
      name: 'Downtown SLC Project',
      address: '123 Main St, Salt Lake City, UT 84101',
      coordinates: {
        lat: 40.7608,
        lng: -111.891
      }
    },
    material: 'Gravel',
    truckType: 'Dump Truck',
    goals: {
      loads: 8,
      tonnage: 240
    },
    foreman: {
      name: 'John Smith',
      phone: '555-123-4567'
    },
    notes: {
      contractor: 'Find job foreman upon arrival. Ask for site safety briefing before unloading.',
      dispatch: 'Use driver messaging to pass along road closures. North entrance closed due to construction.'
    },
    status: 'pending',
    ticketNumber: '500001',
    geoPunchRequired: false,
    geoPunchDistance: 150
  },
  // Upcoming Job
  {
    id: 'job-4',
    date: tomorrowString,
    customer: 'Mountain Development LLC',
    jobName: 'Residential Development Phase 2',
    jobNumber: 'RES-2024-003',
    startTime: '07:30',
    travelTime: '20',
    loadLocation: {
      name: 'Gravel Yard East',
      address: '2200 E 4500 S, Salt Lake City, UT 84117',
      coordinates: {
        lat: 40.6789,
        lng: -111.8123
      }
    },
    dumpLocation: {
      name: 'Sugar House Development',
      address: '2100 S 1100 E, Salt Lake City, UT 84106',
      coordinates: {
        lat: 40.7245,
        lng: -111.8659
      }
    },
    material: 'Road Base',
    truckType: 'Side Dump',
    goals: {
      loads: 10,
      tonnage: 300
    },
    foreman: {
      name: 'Sarah Williams',
      phone: '555-345-6789'
    },
    notes: {
      contractor: 'Enter from the south entrance only. Site has limited space for turning around.',
      dispatch: 'First load needs to arrive by 8:00 AM sharp. Multiple trucks scheduled for this job.'
    },
    status: 'pending'
  }];
  const todaysJobs = jobs.filter(job => job.date === today);
  const upcomingJobs = jobs.filter(job => job.date > today && job.status !== 'completed');
  const handleClockInOut = () => {
    setClockedIn(!clockedIn);
    // In a real app, you would send this status to your backend
    console.log(clockedIn ? 'Clocking out' : 'Clocking in');
  };
  const handleStartJob = (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      setCurrentJobId(job.id);
      setShowJobTracking(true); // Show job tracking screen directly
    }
  };
  const handleCompleteJob = (jobId: string) => {
    setCurrentJobId(null);
    setShowJobTracking(false);
    // In a real app, you would update the job status in your backend
    // Reset job availability to prevent creating a secondary timecard
    const updatedJobs = jobs.map(job => job.id === jobId ? {
      ...job,
      status: 'completed',
      available: false
    } : job);
    // In a real app, you would save these updated jobs to your backend
    console.log(`Job ${jobId} completed and marked as unavailable`);
    // Travel time would be automatically added to the timecard
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      console.log(`Completing job ${jobId} - Adding ${job.travelTime} minutes of travel time to timecard`);
    }
  };
  const handleClockIn = () => {
    if (selectedJob) {
      setCurrentJobId(selectedJob.id);
      setShowGeoClockIn(false);
      // In a real app, you would log the clock in time and location to your backend
      console.log(`Clocked in for job ${selectedJob.id} with geo verification`);
    }
  };
  const handleCancelClockIn = () => {
    setShowGeoClockIn(false);
    setSelectedJob(null);
  };
  const handleCloseJobTracking = () => {
    setShowJobTracking(false);
    setCurrentJobId(null);
    setSelectedJob(null);
  };
  const handleViewMap = (location: {
    lat: number;
    lng: number;
  }) => {
    // In a real app, this would open the map in a native map app
    const url = `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
    window.open(url, '_blank');
  };
  const handleSendMessage = (jobId: string) => {
    // Get the foreman contact for this job
    const jobContacts = getJobContacts(jobId);
    const foremanContact = jobContacts.find(c => c.participantRole === 'foreman');
    if (foremanContact) {
      setShowMessageCenter(true);
      setInitialContactId(foremanContact.participantId);
    } else {
      console.log(`No foreman found for job ${jobId}`);
      alert('No foreman contact found for this job');
    }
  };
  const handleTruckChange = (newTruckNumber: string) => {
    setTruckNumber(newTruckNumber);
    setShowTruckSelector(false);
    // In a real app, you would update this in your backend
    console.log(`Truck number updated to: ${newTruckNumber}`);
  };
  // If the job tracking screen is shown, render only that
  if (showJobTracking && selectedJob) {
    return <JobTrackingScreen job={selectedJob} onClose={handleCloseJobTracking} onComplete={handleCompleteJob} data-id="element-2237" />;
  }
  return <div className="p-4 lg:p-6 pt-16 lg:pt-6 max-w-4xl mx-auto bg-gray-50 min-h-screen" data-id="element-2238">
      <div className="flex justify-between items-center mb-4" data-id="element-2239">
        <h1 className="text-xl lg:text-2xl font-bold" data-id="element-2240">Driver Dashboard</h1>
        <div className="flex items-center gap-2" data-id="element-2241">
          <JobMessagingNotification jobId={currentJobId || undefined} data-id="element-2242" />
        </div>
      </div>

      {/* Safety Alert */}
      {showSafetyAlert && <SafetyAlert onClose={() => setShowSafetyAlert(false)} data-id="element-2243" />}

      {/* Tab Navigation */}
      <div className="flex border-b mb-4 overflow-x-auto" data-id="element-2244">
        <button className={`py-2 px-4 font-medium whitespace-nowrap ${activeTab === 'today' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`} onClick={() => setActiveTab('today')} data-id="element-2245">
          Today's Jobs
        </button>
        <button className={`py-2 px-4 font-medium whitespace-nowrap ${activeTab === 'upcoming' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`} onClick={() => setActiveTab('upcoming')} data-id="element-2246">
          Upcoming Jobs
        </button>
        <button className={`py-2 px-4 font-medium whitespace-nowrap ${activeTab === 'completed' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`} onClick={() => setActiveTab('completed')} data-id="element-2247">
          <div className="flex items-center gap-1" data-id="element-2248">
            <HistoryIcon size={16} data-id="element-2249" />
            Completed Jobs
          </div>
        </button>
      </div>

      {/* Job List */}
      {activeTab === 'today' && <div className="space-y-4" data-id="element-2250">
          <h2 className="text-lg font-semibold flex items-center gap-2" data-id="element-2251">
            <ClipboardListIcon size={18} data-id="element-2252" />
            Today's Jobs
          </h2>
          {todaysJobs.length === 0 ? <div className="bg-white rounded-lg shadow p-6 text-center" data-id="element-2253">
              <TruckIcon size={40} className="mx-auto text-gray-400 mb-2" data-id="element-2254" />
              <p className="text-gray-600" data-id="element-2255">No jobs scheduled for today</p>
            </div> : todaysJobs.map(job => <JobCard key={job.id} job={job} onStartJob={handleStartJob} onCompleteJob={handleCompleteJob} onViewMap={handleViewMap} onCallForeman={() => handleSendMessage(job.id)} isActive={job.id === currentJobId} data-id="element-2256" />)}
        </div>}

      {activeTab === 'upcoming' && <div className="space-y-4" data-id="element-2257">
          <h2 className="text-lg font-semibold flex items-center gap-2" data-id="element-2258">
            <CalendarIcon size={18} data-id="element-2259" />
            Upcoming Jobs
          </h2>
          {upcomingJobs.length === 0 ? <div className="bg-white rounded-lg shadow p-6 text-center" data-id="element-2260">
              <CalendarIcon size={40} className="mx-auto text-gray-400 mb-2" data-id="element-2261" />
              <p className="text-gray-600" data-id="element-2262">No upcoming jobs scheduled</p>
            </div> : upcomingJobs.map(job => <JobCard key={job.id} job={job} onStartJob={handleStartJob} onCompleteJob={handleCompleteJob} onViewMap={handleViewMap} onCallForeman={() => handleSendMessage(job.id)} isActive={false} isUpcoming={true} data-id="element-2263" />)}
        </div>}

      {activeTab === 'completed' && <div className="space-y-4" data-id="element-2264">
          <h2 className="text-lg font-semibold flex items-center gap-2" data-id="element-2265">
            <FileTextIcon size={18} data-id="element-2266" />
            Completed Jobs
          </h2>
          <CompletedJobs jobs={completedJobs} data-id="element-2267" />
        </div>}

      {/* Driver Information */}
      <div className="mt-6 bg-white rounded-lg shadow p-4" data-id="element-2268">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" data-id="element-2269">
          <UserIcon size={18} data-id="element-2270" />
          Driver Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2271">
          <div data-id="element-2272">
            <p className="text-sm text-gray-500" data-id="element-2273">Driver Name</p>
            <p className="font-medium" data-id="element-2274">Mike Rodriguez</p>
          </div>
          <div data-id="element-2275">
            <p className="text-sm text-gray-500" data-id="element-2276">Truck Number</p>
            <div className="flex items-center gap-2" data-id="element-2277">
              <p className="font-medium" data-id="element-2278">{truckNumber}</p>
              <button onClick={() => setShowTruckSelector(true)} className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50" title="Change truck" data-id="element-2279">
                <PencilIcon size={14} data-id="element-2280" />
              </button>
            </div>
          </div>
          <div data-id="element-2281">
            <p className="text-sm text-gray-500" data-id="element-2282">Trucking Company</p>
            <p className="font-medium" data-id="element-2283">Mountain Valley Trucking</p>
          </div>
        </div>
      </div>

      {/* Geo Clock In Screen - Only shown when needed */}
      {showGeoClockIn && selectedJob && <GeoClockInScreen job={selectedJob} onClockIn={handleClockIn} onCancel={handleCancelClockIn} data-id="element-2284" />}

      {/* Truck Selector Modal */}
      {showTruckSelector && <TruckSelector currentTruckNumber={truckNumber} onTruckChange={handleTruckChange} onClose={() => setShowTruckSelector(false)} data-id="element-2285" />}
    </div>;
};